import { checkPassword } from 'models';
import { useCallback } from 'react';
import { useState } from 'react';
import {
  PASSWORD_REQUIRED_CHARACTER_TYPE,
  PasswordHashProperty,
  PasswordRequiredCharacterType,
} from 'shared';

const { containLowerCase, containNumber, containSymbol, containUpperCase } =
  checkPassword;

const { lowerCase, upperCase, symbol, number } =
  PASSWORD_REQUIRED_CHARACTER_TYPE;

type ValidationState = {
  valid: boolean;
};

type PasswordChecks = {
  length: ValidationState;
  lowerCase?: ValidationState;
  upperCase?: ValidationState;
  number?: ValidationState;
  symbol?: ValidationState;
};

type UsePasswordChecksReturnType = {
  passwordChecks: PasswordChecks;
  onUpdateValue: (newValue: string) => void;
};

export const usePasswordChecks = (
  property: PasswordHashProperty,
  initialValue = '',
): UsePasswordChecksReturnType => {
  const { minLength, maxLength, requiredCharacterTypes } = property;
  const checkLength = useCallback(
    (value: string) => checkPassword.checkLength(minLength, maxLength)(value),
    [maxLength, minLength],
  );
  const getCheckResultOrUndefined = useCallback(
    (characterType: PasswordRequiredCharacterType, valid: boolean) =>
      requiredCharacterTypes.includes(characterType)
        ? {
            valid,
          }
        : undefined,
    [requiredCharacterTypes],
  );
  const [passwordChecks, setPasswordChecks] = useState({
    length: {
      valid: checkLength(initialValue),
    },
    lowerCase: getCheckResultOrUndefined(
      lowerCase,
      containLowerCase(initialValue),
    ),
    upperCase: getCheckResultOrUndefined(
      upperCase,
      containUpperCase(initialValue),
    ),
    symbol: getCheckResultOrUndefined(symbol, containSymbol(initialValue)),
    number: getCheckResultOrUndefined(number, containNumber(initialValue)),
  });

  const onUpdateValue = useCallback(
    (newValue = '') => {
      setPasswordChecks({
        length: {
          valid: checkLength(newValue),
        },
        lowerCase: getCheckResultOrUndefined(
          lowerCase,
          containLowerCase(newValue),
        ),
        upperCase: getCheckResultOrUndefined(
          upperCase,
          containUpperCase(newValue),
        ),
        symbol: getCheckResultOrUndefined(symbol, containSymbol(newValue)),
        number: getCheckResultOrUndefined(number, containNumber(newValue)),
      });
    },
    [checkLength, getCheckResultOrUndefined],
  );

  return {
    passwordChecks,
    onUpdateValue,
  };
};
